<script>
/**
 * ResizablePanel
 *
 * Generic collapsible, vertically-resizable panel shell.
 * Renders a drag handle at the top edge and slots content below.
 *
 * Props:
 *   defaultHeight  number   Initial height in px            (default 180)
 *   minHeight      number   Minimum draggable height in px  (default 80)
 *   maxHeight      number   Maximum draggable height in px  (default 600)
 *   headerHeight   number   Height when collapsed in px     (default 44)
 *   cssVar         string   CSS custom property to write the current height to,
 *                           e.g. '--midi-panel-height'. null = no write.
 *   startCollapsed boolean  Whether to start in collapsed state (default false)
 *
 * Slots:
 *   default  — the panel content
 *
 * Exposes (for parent template refs if needed):
 *   collapsed  boolean
 *   height     number
 */

const MIN_HEIGHT_DEFAULT  = 80;
const MAX_HEIGHT_DEFAULT  = 600;
const DEFAULT_HEIGHT      = 180;
const HEADER_HEIGHT       = 44;

export default {
  name: 'ResizablePanel',

  props: {
    defaultHeight: {
      type: Number,
      default: DEFAULT_HEIGHT,
    },
    minHeight: {
      type: Number,
      default: MIN_HEIGHT_DEFAULT,
    },
    maxHeight: {
      type: Number,
      default: MAX_HEIGHT_DEFAULT,
    },
    headerHeight: {
      type: Number,
      default: HEADER_HEIGHT,
    },
    cssVar: {
      type: String,
      default: null,
    },
    startCollapsed: {
      type: Boolean,
      default: false,
    },
    resizable: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    const h = this.startCollapsed ? this.headerHeight : this.defaultHeight;
    return {
      collapsed:           this.startCollapsed,
      panelHeight:         h,
      heightBeforeCollapse: this.defaultHeight,
      dragging:            false,
      dragStartY:          0,
      dragStartHeight:     this.defaultHeight,
    };
  },

  mounted() {
    this._writeCssVar(this.panelHeight);
  },

  beforeUnmount() {
    this._stopDrag();
  },

  methods: {
    // ── Collapse / expand ───────────────────────────────────────

    toggleCollapsed() {
      if (this.collapsed) {
        this.collapsed   = false;
        this.panelHeight = this.heightBeforeCollapse;
      } else {
        this.heightBeforeCollapse = this.panelHeight;
        this.collapsed   = true;
        this.panelHeight = this.headerHeight;
      }
      this._writeCssVar(this.panelHeight);
    },

    // ── Drag-to-resize ──────────────────────────────────────────

    startDrag(event) {
      if (this.collapsed) return;
      this.dragging        = true;
      this.dragStartY      = event.clientY;
      this.dragStartHeight = this.panelHeight;
      window.addEventListener('mousemove', this._onDrag);
      window.addEventListener('mouseup',   this._stopDrag);
      event.preventDefault();
    },

    _onDrag(event) {
      if (!this.dragging) return;
      const delta = this.dragStartY - event.clientY;
      const h = Math.min(this.maxHeight, Math.max(this.minHeight, this.dragStartHeight + delta));
      this.panelHeight         = h;
      this.heightBeforeCollapse = h;
      this._writeCssVar(h);
    },

    _stopDrag() {
      if (!this.dragging) return;
      this.dragging = false;
      window.removeEventListener('mousemove', this._onDrag);
      window.removeEventListener('mouseup',   this._stopDrag);
    },

    // ── Helpers ─────────────────────────────────────────────────

    _writeCssVar(value) {
      if (this.cssVar) {
        document.documentElement.style.setProperty(this.cssVar, value + 'px');
      }
    },
  },
};
</script>

<template>
  <div
    class="resizable-panel"
    :style="{ height: panelHeight + 'px' }"
  >
    <!-- Drag handle — hidden when collapsed or resizing disabled -->
    <div
      v-if="!collapsed && resizable"
      class="resizable-panel__handle"
      :class="{ 'resizable-panel__handle--active': dragging }"
      title="Drag to resize"
      @mousedown="startDrag"
    />

    <!-- Slotted content -->
    <div class="resizable-panel__content">
      <slot :collapsed="collapsed" :toggle-collapsed="toggleCollapsed" />
    </div>
  </div>
</template>
